import { useEffect, useState } from "react";
import type { AirtimeAllocation } from "../types";
import AirtimeEmptyState from "./AirtimeEmptyState";

export default function AirtimeAllocationTable({
  data,
  onSave,
  savingEmployeeId,
}: {
  data: AirtimeAllocation[];
  onSave: (employeeId: number, amount: number) => void;
  savingEmployeeId?: number | null;
}) {
  if (!data.length) {
    return (
      <AirtimeEmptyState
        title="No employees found"
        description="No active employees matched your search."
      />
    );
  }

  return (
    <div className="overflow-hidden rounded-3xl border border-slate-200 bg-white shadow-sm">
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-slate-200">
          <thead className="bg-slate-50">
            <tr>
              <TableHead>Employee</TableHead>
              <TableHead>Primary Phone</TableHead>
              <TableHead>Monthly Airtime</TableHead>
              <TableHead>Distribution</TableHead>
              <TableHead>Status</TableHead>
              <TableHead align="right">Action</TableHead>
            </tr>
          </thead>

          <tbody className="divide-y divide-slate-100">
            {data.map((row) => (
              <AllocationRow
                key={row.employeeId}
                row={row}
                saving={savingEmployeeId === row.employeeId}
                onSave={onSave}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AllocationRow({
  row,
  saving,
  onSave,
}: {
  row: AirtimeAllocation;
  saving?: boolean;
  onSave: (employeeId: number, amount: number) => void;
}) {
  const [amount, setAmount] = useState(row.monthlyAmount);

  useEffect(() => {
    setAmount(row.monthlyAmount);
  }, [row.monthlyAmount]);

  const phoneCount = (row.mobilePhone ? 1 : 0) + (row.alternativePhone ? 1 : 0);
  const hasPhone = phoneCount > 0;

  return (
    <tr className="hover:bg-slate-50">
      <td className="px-6 py-4">
        <div className="font-black text-slate-900">{row.employeeName}</div>
        <div className="mt-1 text-xs font-semibold text-slate-500">
          Phone Numbers: {phoneCount}
        </div>
      </td>

      <td className="px-6 py-4 text-sm font-semibold text-slate-700">
        {row.mobilePhone || "-"}
      </td>

      <td className="px-6 py-4">
        <input
          type="number"
          min={0}
          value={amount === 0 ? "" : amount}
          onFocus={(e) => { if (amount === 0) e.target.value = ""; }}
          onChange={(e) => setAmount(e.target.value === "" ? 0 : Number(e.target.value))}
          onBlur={(e) => { if (e.target.value === "") setAmount(0); }}
          placeholder="0"
          className="w-40 rounded-2xl border border-slate-300 px-4 py-2 text-sm outline-none transition focus:border-emerald-500 focus:ring-4 focus:ring-emerald-100"
        />
      </td>

      <td className="px-6 py-4">
        {row.distributions?.length ? (
          <span className="inline-block whitespace-nowrap rounded-full bg-blue-50 px-3 py-1 text-xs font-black text-blue-700">
            {row.distributions.length} number(s)
          </span>
        ) : (
          <span className="inline-block whitespace-nowrap rounded-full bg-slate-100 px-3 py-1 text-xs font-black text-slate-600">
            Primary only
          </span>
        )}
      </td>

      <td className="px-6 py-4">
        <span
          className={[
            "rounded-full px-3 py-1 text-xs font-black",
            row.active
              ? "bg-emerald-100 text-emerald-700"
              : "bg-slate-100 text-slate-600",
          ].join(" ")}
        >
          {row.active ? "ACTIVE" : "NOT SET"}
        </span>

        {!hasPhone ? (
          <div className="mt-2 text-xs font-bold text-red-600">
            Missing phone number
          </div>
        ) : null}
      </td>

      <td className="px-6 py-4 text-right">
        <button
          type="button"
          disabled={saving || amount < 0}
          onClick={() => onSave(row.employeeId, amount)}
          className="rounded-2xl bg-emerald-600 px-4 py-2 text-sm font-black text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50"
        >
          {saving ? "Saving..." : "Save"}
        </button>
      </td>
    </tr>
  );
}

function TableHead({
  children,
  align = "left",
}: {
  children: React.ReactNode;
  align?: "left" | "right";
}) {
  return (
    <th
      className={[
        "px-6 py-4 text-xs font-black uppercase tracking-wide text-slate-600",
        align === "right" ? "text-right" : "text-left",
      ].join(" ")}
    >
      {children}
    </th>
  );
}